import { type ReactNode, useMemo, useRef } from 'react';
import { useRxCollection, useRxDocument } from 'rxdb/plugins/react';
import { useIntl } from 'react-intl';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';
import { type ProjectDocType } from '@ast-data/shared';
import { Link as RouterLink } from 'react-router-dom';
import { projectMeasureNumber } from '../features/projects/projectTitle';
import { APP_BAR_TITLE_SX } from './appBarTitle';
import { linkClick } from './routerLink';
import type { RecordSiblings, SiblingLink } from './useRecordSiblings';
import { useRecordShortcuts } from './recordShortcuts';

/** A middle breadcrumb crumb: the Befund a Planum/Profil description belongs to. */
export interface FindingCrumb {
    /** The Befundnummer to show in the chip. */
    number: string;
    /** Jump to that Befund's own editor (guard-aware). */
    onOpen: () => void;
    /**
     * That editor's route. Given, the chip renders as a real link and can be
     * middle-clicked into a new tab; `onOpen` still handles the plain click.
     */
    to?: string;
}

/** A labelled "back" to the dialog the user came from (see `useDialogBackCrumb`). */
export interface BackCrumb {
    /** What the previous dialog was, e.g. "Planum 1" or "Befund 5". */
    label: string;
    /** Return to it (guard-aware). */
    onOpen: () => void;
}

/**
 * The fullscreen record dialogs' app bar: the Maßnahmennummer, a breadcrumb
 * divider, the dialog title, the close button. A description dialog
 * (Planum/Profil) inserts a clickable "Befund {Nr}" chip between the project
 * and the title. The project part yields to the title when space runs out.
 */
export default function FormDialogAppBar({
    projectId,
    title,
    onClose,
    startSlot,
    findingCrumb,
    backCrumb,
    siblings
}: {
    projectId: string;
    title: ReactNode;
    onClose: () => void;
    // Leading affordance before the project breadcrumb, e.g. the mobile
    // section-nav Menu button (see SectionNavMenuButton).
    startSlot?: ReactNode;
    findingCrumb?: FindingCrumb;
    backCrumb?: BackCrumb;
    // The record's neighbours in its grid's default order, drawn left and
    // right of the title (see {@link useRecordSiblings}).
    siblings?: RecordSiblings;
}) {
    const intl = useIntl();
    const projectsCollection = useRxCollection<ProjectDocType>('projects');
    const { result: projectDoc } = useRxDocument<ProjectDocType>(
        projectsCollection,
        projectId
    );
    const projects = useMemo(
        () =>
            projectDoc ? (projectDoc.toMutableJSON() as ProjectDocType) : null,
        [projectDoc]
    );
    const projectLabel = projectMeasureNumber(projects);

    // Strg+←/→ pages like the arrows, Strg+↑ follows the Befund chip.
    const barRef = useRef<HTMLElement>(null);
    useRecordShortcuts(barRef, {
        previous: siblings?.previous?.onOpen,
        next: siblings?.next?.onOpen,
        finding: findingCrumb?.onOpen
    });

    // Its label truncates rather than pushing the layout around.
    const backButton = backCrumb && (
        <Button
            color="inherit"
            startIcon={<ArrowBackIcon />}
            onClick={backCrumb.onOpen}
            sx={{
                minWidth: 0,
                flexShrink: 1,
                textTransform: 'none',
                color: 'text.secondary',
                '& .MuiButton-startIcon': { mr: 0.5 },
                '& .label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }
            }}
        >
            <Box component="span" className="label">
                {backCrumb.label}
            </Box>
        </Button>
    );

    /**
     * A step to the record before or after this one. A real link, so it can be
     * opened in a new tab; the plain click goes through `onOpen`, the
     * unsaved-changes guard's way out (see linkClick).
     *
     * The chevron sits on the TITLE side of the label, so the pair reads as one
     * line of records with the open one in the middle: `02.08. ‹ 01.08. › 31.07.`
     */
    const siblingButton = (
        link: SiblingLink,
        direction: 'previous' | 'next'
    ) => {
        const title = intl.formatMessage(
            { id: `common.${direction}Record` },
            { label: link.label }
        );
        const chevron =
            direction === 'previous' ? (
                <ChevronLeftIcon />
            ) : (
                <ChevronRightIcon />
            );
        return (
            <Tooltip title={title}>
                <Button
                    component={RouterLink}
                    to={link.to}
                    onClick={linkClick(link.onOpen)}
                    color="inherit"
                    aria-label={title}
                    {...(direction === 'previous'
                        ? { endIcon: chevron }
                        : { startIcon: chevron })}
                    sx={{
                        minWidth: 0,
                        flexShrink: 0,
                        px: 0.5,
                        textTransform: 'none',
                        color: 'text.secondary',
                        // Below `lg` the buttons are chevrons only, so no
                        // margin towards a label that isn't drawn.
                        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                            mx: 0,
                            ...(direction === 'previous'
                                ? { ml: { xs: 0, lg: 0.5 } }
                                : { mr: { xs: 0, lg: 0.5 } })
                        },
                        // The names appear from `lg` (1200px) on: the
                        // Maßnahmennummer, the title and TWO record names
                        // compete for this row, and below that width the title
                        // has to win. The chevrons keep their tooltip.
                        '& .label': {
                            display: { xs: 'none', lg: 'block' },
                            maxWidth: '12ch',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }
                    }}
                >
                    <Box component="span" className="label">
                        {link.label}
                    </Box>
                </Button>
            </Tooltip>
        );
    };

    const closeButton = (
        <IconButton
            edge="end"
            onClick={onClose}
            aria-label={intl.formatMessage({ id: 'common.cancel' })}
        >
            <CloseIcon />
        </IconButton>
    );

    // Shared by both layouts. Every part steps down together on a phone (see
    // APP_BAR_TITLE_SX) — sizing only the title would leave the crumbs
    // towering over it.
    const breadcrumb = (
        <>
            {projectLabel && (
                <>
                    <Typography
                        variant="h6"
                        noWrap
                        sx={{
                            ...APP_BAR_TITLE_SX,
                            color: 'text.secondary',
                            flexShrink: 1
                        }}
                    >
                        {projectLabel}
                    </Typography>
                    <Typography
                        variant="h6"
                        aria-hidden
                        sx={{ ...APP_BAR_TITLE_SX, color: 'text.secondary' }}
                    >
                        ›
                    </Typography>
                </>
            )}
            {findingCrumb && (
                <>
                    <Tooltip
                        title={intl.formatMessage({
                            id: 'finding.crumbShortcut'
                        })}
                    >
                        <Chip
                            color="primary"
                            label={intl.formatMessage(
                                { id: 'finding.crumb' },
                                { number: findingCrumb.number || '?' }
                            )}
                            {...(findingCrumb.to
                                ? { component: RouterLink, to: findingCrumb.to }
                                : {})}
                            clickable
                            onClick={
                                findingCrumb.to
                                    ? linkClick(findingCrumb.onOpen)
                                    : findingCrumb.onOpen
                            }
                            sx={{
                                flexShrink: 0,
                                height: { xs: 32, sm: 40 },
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                fontWeight: 700,
                                px: 0.5
                            }}
                        />
                    </Tooltip>
                    <Typography
                        variant="h6"
                        aria-hidden
                        sx={{ ...APP_BAR_TITLE_SX, color: 'text.secondary' }}
                    >
                        ›
                    </Typography>
                </>
            )}
            <Typography variant="h6" noWrap sx={APP_BAR_TITLE_SX}>
                {title}
            </Typography>
        </>
    );

    /**
     * Whether the phone bar needs its second row. A record editor does:
     * `2024-118 › Bef. 7 › Planum 1` beside a back button, a section menu and
     * the X does not fit one phone line. A dialog carrying nothing but its own
     * title has nothing to wrap, and the empty first row would be a line of
     * height spent on one button.
     *
     * Decided on `projectId`, NOT on the label it resolves to: the label
     * arrives from the local database a moment after the first render, and a
     * bar that grew from one row to two would jump exactly during the
     * loading-shell → form swap the shell exists to make seamless.
     */
    const twoRowsOnPhone = Boolean(
        startSlot || backButton || findingCrumb || projectId
    );

    return (
        <AppBar
            ref={barRef}
            position="relative"
            color="inherit"
            elevation={0}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
            {/* Phones: buttons on top, breadcrumb below — unless there is only
                a title, which shares the one row with the X. */}
            <Toolbar
                sx={{
                    display: { xs: 'flex', sm: 'none' },
                    gap: twoRowsOnPhone ? 0.5 : 1,
                    ...(twoRowsOnPhone
                        ? {
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              py: 1
                          }
                        : {})
                }}
            >
                {twoRowsOnPhone ? (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 0.5
                            }}
                        >
                            <Box
                                sx={{
                                    minWidth: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}
                            >
                                {startSlot}
                                {backButton}
                            </Box>
                            {closeButton}
                        </Box>
                        <Box
                            sx={{
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1,
                                rowGap: 0.5
                            }}
                        >
                            {breadcrumb}
                        </Box>
                    </>
                ) : (
                    <>
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            {breadcrumb}
                        </Box>
                        {closeButton}
                    </>
                )}
            </Toolbar>

            {/* Tablet/desktop: one centred row; equal-flex sides keep the
                breadcrumb in the true centre. */}
            <Toolbar sx={{ gap: 1, display: { xs: 'none', sm: 'flex' } }}>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                >
                    {startSlot}
                    {backButton}
                </Box>
                {/* The pager is desktop-only: the phone toolbar is already two
                    rows, and a record is stepped through at a desk, not in a
                    trench. */}
                <Box
                    sx={{
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1
                    }}
                >
                    {siblings?.previous &&
                        siblingButton(siblings.previous, 'previous')}
                    {breadcrumb}
                    {siblings?.next && siblingButton(siblings.next, 'next')}
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'flex-end'
                    }}
                >
                    {closeButton}
                </Box>
            </Toolbar>
        </AppBar>
    );
}
